/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains a hook that proxies a hook from 
 * online-patient-management-reducers making less types required to use the hook.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import * as React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { faTimes } from '@fortawesome/pro-duotone-svg-icons/faTimes';

import { makeStyles, Theme } from '@material-ui/core/styles';

import { useField, useForm } from 'react-final-form';

import Button from '@material-ui/core/Button';

import Table from '@material-ui/core/Table';

import TableHead from '@material-ui/core/TableHead';

import TableCell from '@material-ui/core/TableCell';

import TableRow from '@material-ui/core/TableRow';

import TableBody from '@material-ui/core/TableBody';

import Typography from '@material-ui/core/Typography';
/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */
// import FormGridContext from '../../../../context/FormGridContext';
import ConditionalContext from 'src/context/ConditionalContext';

import OpmsFieldLabel from '../../OpmsFieldLabel';

import { pascalToCamelCase } from '../../OpmsInput';

import OpmsConditional, { IOpmsGridConditionParams } from '../../OpmsConditional';

import { FormSubscription } from 'final-form';

import LookupsContext from '../../../../context/LookupsContext';

import LookupContext from '../../../../context/LookupContext';

import FieldContext from '../../../../context/FieldContext';

import FormGridRowErrorContext from '../../../../context/FormGridErrorContext';

import OpmsFormGridRowErrors from './OpmsFormGridRowErrors';

import DisableFormContext from '../../../../context/DisableFormContext';
import FieldErrorContext from '../../../../context/FieldErrorContext';
import OpmsFieldErrors from '../../OpmsFieldErrors';

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */
interface IOpmsFormGridProps {
    name: string;
    columns: IOpmsFormGridCell[];
    allowRemove?: boolean;
    hideRemove?: (index: number) => void;
    allowAdd?: boolean;
}

interface IOpmsFormGridStyleProps {
    hidden: boolean;
}

export interface IOpmsFormGridCell<FormType extends object = any> extends IOpmsFormGridHeaderStyleProps {
    name: string;
    header?: React.ReactNode;
    content: React.ReactNode;
    condition?: (parameters: IOpmsGridConditionParams<FormType>) => boolean;
    subscription?: FormSubscription;
}

interface IOpmsFormGridHeaderStyleProps {
    width?: string | number;
    minWidth?: string | number;
}


interface IOpmsFormGridRow {
    parentName: string;
    rowName: string;
    index: number;
    allowRemove?: boolean;
    hideRemove?: (index: number) => void;
    remove: (index: number) => void;
    columns: IOpmsFormGridCell[];
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const useStyles = makeStyles<Theme, IOpmsFormGridStyleProps>(theme => ({
    container: {
        display: props => props.hidden ? 'none' : 'block',
        overflowX: 'auto'
    },
    table: {
        minWidth: '100%'
    },
    addButton: {
        textAlign: 'right',
        padding: theme.spacing(3)
    },
    removeCell: {
        width: 95
    }
}));

const useHeaderStyles = makeStyles<Theme, IOpmsFormGridHeaderStyleProps>(theme => ({
    header: {
        width: props => props.width ? props.width : undefined,
        minWidth: props => props.minWidth ? props.minWidth : undefined
    }
}));

const useRowStyles = makeStyles<Theme>(theme => ({
    remove: {
        color: theme.palette.error.main
    }
}));


/*
 * ---------------------------------------------------------------------------------
 * components
 * ---------------------------------------------------------------------------------
 */

const OpmsFormGridHeader: React.FunctionComponent<IOpmsFormGridCell & { parentName: string; }> = ({
    name,
    content,
    header,
    parentName,
    width,
    minWidth,
    children
}) => {
    const classes = useHeaderStyles({ width, minWidth });

    return (
        <TableCell className={classes.header}>
            {
                header ?
                    header :
                    <OpmsFieldLabel name={`${parentName}.${name}`} bold />
            }
        </TableCell>
    )
}

const OpmsFormGridCell: React.FunctionComponent<IOpmsFormGridCell & { parentName: string; rowName: string; index: number }> = (props) => {
    if (!props.name) {
        return null;
    }

    return (
        <InnerOpmsFormGridCell {...props} />
    )
}

const InnerOpmsFormGridCell: React.FunctionComponent<IOpmsFormGridCell & { parentName: string; rowName: string; index: number }> = ({
    content,
    name,
    parentName,
    rowName,
    children
}) => {
    const lookups = React.useContext(LookupsContext);

    const lookup = React.useMemo(() => {
        return lookups?.find(l => l.propertyName === `${parentName}.${name}`) ?? null;
    }, [parentName, name, lookups])

    const conditionalResult = React.useContext(ConditionalContext)

    const fieldName = React.useMemo(() => pascalToCamelCase(name), [name]);

    const { input: { onChange } } = useField(fieldName, {});

    React.useEffect(() => {
        if (conditionalResult?.result === false) {
            onChange(undefined);
        }
    }, [onChange, conditionalResult?.result])

    if (conditionalResult?.mode === 'Show' && conditionalResult?.result === false) {
        return null as any;
    }

    return (
        <TableCell>
            <LookupContext.Provider value={lookup}>
                <FieldContext.Provider value={`${rowName}.${name}`}>
                    {content}
                </FieldContext.Provider>
            </LookupContext.Provider>
        </TableCell>
    )
}

function OpmsFromGridRow({
    columns,
    parentName,
    rowName,
    allowRemove,
    hideRemove,
    remove,
    index
}: IOpmsFormGridRow) {

    const classes = useRowStyles();

    const [errorsExpanded, setErrorsExpanded] = React.useState(true);

    const rowErrorState = React.useMemo(() => {
        return {
            expanded: errorsExpanded,
            setExpanded: setErrorsExpanded
        }
    }, [errorsExpanded, setErrorsExpanded]);

    const calculatedFields: string[] = React.useMemo(() => {
        return Array.from(new Set(columns.filter(c => !!c.name).map(c => `${rowName}.${c.name}`) as string[]));
    }, [columns, rowName])

    const onRemoveClick = React.useCallback(() => {
        remove(index);
    }, [remove, index])

    const formDisabled = React.useContext(DisableFormContext);

    return (
        <FormGridRowErrorContext.Provider value={rowErrorState}>
            <TableRow>
                {
                    columns.map((column, i) => {
                        const fieldName = `${rowName}.${column.name}`;

                        if (!column.name) {
                            return (
                                <TableCell
                                    key={i}
                                />
                            );
                        }

                        if (column.condition) {
                            return (
                                <OpmsConditional
                                    key={fieldName}
                                    condition={column.condition}
                                    subscription={column.subscription ?? {}}
                                    mode="Enable"
                                    index={index}
                                >
                                    <OpmsFormGridCell
                                        parentName={parentName}
                                        rowName={rowName}
                                        index={index}
                                        {...column}
                                    />
                                </OpmsConditional>
                            );
                        }

                        return (
                            <OpmsFormGridCell
                                key={fieldName}
                                parentName={parentName}
                                rowName={rowName}
                                index={index}
                                {...column}
                            />
                        );
                    })
                }
                {
                    allowRemove !== false && !formDisabled && (
                        <TableCell>
                            {
                                !(!!hideRemove ? hideRemove(index) : false) && <Button
                                    variant="text"
                                    color="default"
                                    className={classes.remove}
                                    onClick={onRemoveClick}
                                >
                                    <FontAwesomeIcon fixedWidth icon={faTimes} />
                                </Button>
                            }
                        </TableCell>
                    )
                }
            </TableRow>
            <OpmsFormGridRowErrors fields={calculatedFields} columnCount={columns.length + (allowRemove !== false ? 1 : 0)} />
        </FormGridRowErrorContext.Provider>
    )
}


const OpmsFormGrid: React.FunctionComponent<IOpmsFormGridProps> = ({
    columns,
    allowAdd,
    allowRemove,
    hideRemove,
    children,
    name
}) => {
    const conditionalResult = React.useContext(ConditionalContext)

    const disabled = conditionalResult?.result === false && conditionalResult?.mode === 'Enable';
    const hidden = conditionalResult?.result === false && conditionalResult?.mode === 'Show';

    const classes = useStyles({ hidden });

    const fieldName = React.useMemo(() => {

        const splitFieldConst = name.split('.');

        return splitFieldConst.map(s => pascalToCamelCase(s)).join('.');

    }, [name]);

    const form = useForm();

    const remove = React.useCallback((index: number) => {
        form.mutators.remove(fieldName, index);
    }, [fieldName, form.mutators])

    const { input: { value: gridRows } } = useField<any[]>(fieldName, { value: [{}], subscription: { value: true } });
    const { input: { onChange } } = useField<any[]>(fieldName, { subscription: {} });

    React.useEffect(() => {
        if (disabled || hidden) {
            onChange([])
        }
    }, [disabled, hidden, onChange]);

    const [errorsExpanded, setErrorsExpanded] = React.useState(true);

    const fieldErrorState = React.useMemo(() => {
        return {
            expanded: errorsExpanded,
            setExpanded: setErrorsExpanded
        }
    }, [errorsExpanded, setErrorsExpanded]);

    const onAddClick = React.useCallback(() => {
        form.mutators.push(fieldName, {})
    }, [fieldName, form.mutators])

    const formDisabled = React.useContext(DisableFormContext);

    const nonNullGridRow = React.useMemo(() => {
        return gridRows ? gridRows : [];
    }, [gridRows]);

    return (
        <div>
            <FieldErrorContext.Provider value={fieldErrorState}>
                <FieldContext.Provider value={name}>
                    <div
                        className={classes.container}
                    >
                        {
                            nonNullGridRow && (
                                <Table
                                    className={classes.table}
                                >
                                    <TableHead>
                                        <TableRow>
                                            {
                                                columns?.map((header) => {
                                                    return (
                                                        <OpmsFormGridHeader
                                                            key={header.name}
                                                            parentName={name}
                                                            {...header}
                                                        />
                                                    );
                                                })
                                            }
                                            {
                                                allowRemove !== false && !disabled && !formDisabled && (
                                                    <TableCell
                                                        className={classes.removeCell}
                                                    >
                                                        <Typography>
                                                            <strong>
                                                                Remove
                                                    </strong>
                                                        </Typography>
                                                    </TableCell>
                                                )
                                            }
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {
                                            nonNullGridRow.map((values, index) => {
                                                const rowName = `${name}[${index}]`;

                                                return (
                                                    <OpmsFromGridRow
                                                        key={rowName}
                                                        parentName={name}
                                                        columns={columns}
                                                        allowRemove={allowRemove}
                                                        hideRemove={hideRemove}
                                                        remove={remove}
                                                        index={index}
                                                        rowName={rowName}
                                                    />
                                                );
                                            })
                                        }
                                    </TableBody>
                                </Table>
                            )
                        }
                    </div>
                    <OpmsFieldErrors />
                </FieldContext.Provider>
            </FieldErrorContext.Provider>
            {
                allowAdd !== false && !hidden && !formDisabled && (
                    <div
                        className={classes.addButton}
                    >
                        <Button
                            variant="contained"
                            disabled={disabled}
                            type="button"
                            color="primary"
                            onClick={onAddClick}
                        >
                            Add
                        </Button>
                    </div>
                )
            }
        </div>
    )
}

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */

export default OpmsFormGrid;
